import { safeParse, safeParseNullDefault, parseNumberStrict, removeNulls } from 'shouldve-been-js';
import ChecklistType from '../Enums/ChecklistType';
import EditorType from '../Enums/EditorType';
import CategoryChecklistElement from '../Models/Checklists/Elements/CategoryChecklistElementModel';
import CheckboxChecklistElement from '../Models/Checklists/Elements/CheckboxChecklistElementModel';
import type ChecklistElement from '../Models/Checklists/Elements/ChecklistElementModel';
import CountryChecklistElement from '../Models/Checklists/Elements/CountryChecklistElementModel';
import DateChecklistElement from '../Models/Checklists/Elements/DateChecklistElementModel';
import DecimalChecklistElement from '../Models/Checklists/Elements/DecimalChecklistElementModel';
import DepartmentChecklistElement from '../Models/Checklists/Elements/DepartmentChecklistElementModel';
import DropdownChecklistElement from '../Models/Checklists/Elements/DropdownChecklistElementModel';
import HeaderChecklistElement from '../Models/Checklists/Elements/HeaderChecklistElementModel';
import InspectingCompanyChecklistElement from '../Models/Checklists/Elements/InspectingCompanyChecklistElementModel';
import InspectorChecklistElement from '../Models/Checklists/Elements/InspectorChecklistElementModel';
import IntegerChecklistElement from '../Models/Checklists/Elements/IntegerChecklistElementModel';
import LookupChecklistElement from '../Models/Checklists/Elements/LookupChecklistElementModel';
import MaskChecklistElement from '../Models/Checklists/Elements/MaskChecklistElementModel';
import SimpleChecklistElement from '../Models/Checklists/Elements/SimpleChecklistElementModel';
import TextAreaChecklistElement from '../Models/Checklists/Elements/TextAreaChecklistElementModel';
import TextFieldChecklistElement from '../Models/Checklists/Elements/TextFieldChecklistElementModel';
import UserChecklistElement from '../Models/Checklists/Elements/UserChecklistElementModel';

//NOTE: sadly, typescript is not able to resolve the circular reference problem which occurs by putting these methods where they belong (inside ChecklistElementModel), so they are put here

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types, @typescript-eslint/no-explicit-any
export function checklistElementHelperFromJson(key: string, json: any, checklistType: ChecklistType | null): ChecklistElement | null {

    const elementId: string = key;
    const name: string = safeParse<string>(json?.itemName, '');
    const sortNumber: number | null = safeParseNullDefault<number>(json?.sortNumber, 0);
    const status: string = safeParse<string>(json?.status, 'D');
    
    let itemNumber: string | null = safeParseNullDefault<string>(json?.itemNumber, '');
    if (itemNumber === '') {
        itemNumber = null;
    }
    
    if (status === 'D' || sortNumber === null) {
        return null;
    }

    if (checklistType === ChecklistType.CHECKLIST) {

        const hasCheckbox: boolean = json?.options?.editor === 'Check';
        const hasComment: boolean = json?.options?.comment !== 'False';
        const isBold: boolean = json?.options?.style === 'bold';
        return new SimpleChecklistElement(elementId, name, itemNumber, sortNumber, hasCheckbox, hasComment, isBold);

    } else if (checklistType === ChecklistType.CUSTOM) {

        if (json?.options?.editor === 'Check') {
            return new CheckboxChecklistElement(elementId, name, itemNumber, sortNumber);

        } else if (json?.options?.editor === undefined) {
            return new HeaderChecklistElement(elementId, name, itemNumber, sortNumber);

        } else if (json?.options?.editor === 'String') {
            const defaultValue: string | null = safeParseNullDefault<string>(json?.options?.default, '');
            let maxLength: number | null = parseNumberStrict(safeParseNullDefault<string>(json?.options?.properties, ''));
            if (maxLength !== null && maxLength <= 0) {
                maxLength = null;
            }
            return new TextFieldChecklistElement(elementId, name, itemNumber, sortNumber, defaultValue, maxLength);

        } else if (json?.options?.editor === 'Memo') {
            return new TextAreaChecklistElement(elementId, name, itemNumber, sortNumber);

        } else if (json?.options?.editor === 'Mask') {
            const regexMask: string = safeParse<string>(json?.options?.properties, '');
            return new MaskChecklistElement(elementId, name, itemNumber, sortNumber, regexMask);

        } else if (json?.options?.editor === 'Float') {
            return new DecimalChecklistElement(elementId, name, itemNumber, sortNumber);

        } else if (json?.options?.editor === 'Integer') {
            return new IntegerChecklistElement(elementId, name, itemNumber, sortNumber);

        } else if (json?.options?.editor === 'Date') {
            const currentAsDefault: boolean = safeParseNullDefault<string>(json?.options?.default, '') === 'Today';
            return new DateChecklistElement(elementId, name, itemNumber, sortNumber, currentAsDefault);

        } else if (json?.options?.editor === 'Countrylist') {
            return new CountryChecklistElement(elementId, name, itemNumber, sortNumber);

        } else if (json?.options?.editor === 'Inspectorlist') {
            return new InspectorChecklistElement(elementId, name, itemNumber, sortNumber);

        } else if (json?.options?.editor === 'Userlist') {
            const currentAsDefault: boolean = safeParseNullDefault<string>(json?.options?.default, '') === 'IDCURRENTUSER';
            let roleId: string | null = safeParseNullDefault<string>(json?.options?.properties, '');
            if (roleId === '') {
                roleId = null;
            }
            return new UserChecklistElement(elementId, name, itemNumber, sortNumber, currentAsDefault, roleId);

        } else if (json?.options?.editor === 'Combo') {
            const optionsString: string = safeParse<string>(json?.options?.properties, '');
            const defaultValue: string | null = safeParseNullDefault<string>(json?.options?.default, '');
            const itemEnableMaskString: string = safeParse<string>(json?.options?.itemEnableMask, '');
            const dependentElementId: string | null = safeParseNullDefault<string>(json?.options?.dependentItem, '');
            const dependencyMappingString: string = safeParse<string>(json?.options?.dependencyMapping, '');

            const options: string[] = optionsString.split(';');
            let itemEnableMask: boolean[] = itemEnableMaskString === '' ? [] : itemEnableMaskString.split(';').map((itemEnableMaskElement: string) => itemEnableMaskElement === '1');
            const dependencyMapping: string[][] = dependencyMappingString === '' ? [] : dependencyMappingString.split(';').map((dependencyMappingElement: string) => (dependencyMappingElement === '' ? [] : dependencyMappingElement.split('|')));

            if (itemEnableMask.length < options.length) {
                itemEnableMask = itemEnableMask.concat(Array(options.length - itemEnableMask.length).fill(true));
            }

            return new DropdownChecklistElement(elementId, name, itemNumber, sortNumber, options, defaultValue, itemEnableMask, dependentElementId, dependencyMapping);

        } else if (json?.options?.editor === 'InspCompanylist') {
            return new InspectingCompanyChecklistElement(elementId, name, itemNumber, sortNumber);

        } else if (json?.options?.editor === 'Categorylist') {
            const parentCategoryId: string | null = safeParseNullDefault<string>(json?.options?.properties, '');

            if (parentCategoryId === null || parentCategoryId === '') {
                return null;
            }

            return new CategoryChecklistElement(elementId, name, itemNumber, sortNumber, parentCategoryId);

        } else if (json?.options?.editor === 'Departmentlist') {
            const dependentElementId: string | null = safeParseNullDefault<string>(json?.options?.dependentItem, '');
            
            return new DepartmentChecklistElement(elementId, name, itemNumber, sortNumber, dependentElementId);

        } else if (json?.options?.editor === 'DBLookup') {
            const lookupType: string | null = safeParseNullDefault<string>(json?.options?.properties, '');
            const dependentElementId: string | null = safeParseNullDefault<string>(json?.options?.dependentItem, '');

            if (lookupType === null || dependentElementId === null) {
                return null;
            }
            
            return new LookupChecklistElement(elementId, name, itemNumber, sortNumber, lookupType, dependentElementId);

        } else {
            return null;
        }
    } else {
        return null;
    }
}

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types, @typescript-eslint/no-explicit-any
export function checklistElementHelperFromIndexedDB(object: any): ChecklistElement | null {

    const elementId: string | null = safeParseNullDefault<string>(object?._elementId, '');
    const name: string = safeParse<string>(object?._name, '');
    const itemNumber: string | null = safeParseNullDefault<string>(object?._itemNumber, '');
    const sortNumber: number | null = safeParseNullDefault<number>(object?._sortNumber, 0);
    
    if (elementId === null || sortNumber === null) {
        return null;
    }

    if (object?._editorType === EditorType.SIMPLE_CHECKLIST) {
        const hasCheckbox: boolean = safeParse<boolean>(object?._hasCheckbox, false);
        const hasComment: boolean = safeParse<boolean>(object?._hasComment, true);
        const isBold: boolean = safeParse<boolean>(object?._isBold, false);
        return new SimpleChecklistElement(elementId, name, itemNumber, sortNumber, hasCheckbox, hasComment, isBold);

    } else if (object?._editorType === EditorType.CHECKBOX) {
        return new CheckboxChecklistElement(elementId, name, itemNumber, sortNumber);

    } else if (object?._editorType === EditorType.HEADER) {
        return new HeaderChecklistElement(elementId, name, itemNumber, sortNumber);

    } else if (object?._editorType === EditorType.TEXTFIELD) {
        const defaultValue: string | null = safeParseNullDefault<string>(object?._defaultValue, '');
        const maxLength: number | null = safeParseNullDefault<number>(object?._maxLength, 0);
        return new TextFieldChecklistElement(elementId, name, itemNumber, sortNumber, defaultValue, maxLength);

    } else if (object?._editorType === EditorType.TEXTAREA) {
        return new TextAreaChecklistElement(elementId, name, itemNumber, sortNumber);

    } else if (object?._editorType === EditorType.MASK) {
        const regexMask: string = safeParse<string>(object?._regexMask, '');
        return new MaskChecklistElement(elementId, name, itemNumber, sortNumber, regexMask);

    } else if (object?._editorType === EditorType.DECIMAL) {
        return new DecimalChecklistElement(elementId, name, itemNumber, sortNumber);

    } else if (object?._editorType === EditorType.INTEGER) {
        return new IntegerChecklistElement(elementId, name, itemNumber, sortNumber);

    } else if (object?._editorType === EditorType.DATE) {
        const currentAsDefault: boolean = safeParse<boolean>(object?._currentAsDefault, false);
        return new DateChecklistElement(elementId, name, itemNumber, sortNumber, currentAsDefault);

    } else if (object?._editorType === EditorType.COUNTRY) {
        return new CountryChecklistElement(elementId, name, itemNumber, sortNumber);

    } else if (object?._editorType === EditorType.INSPECTOR) {
        return new InspectorChecklistElement(elementId, name, itemNumber, sortNumber);

    } else if (object?._editorType === EditorType.USER) {
        const currentAsDefault: boolean = safeParse<boolean>(object?._currentAsDefault, false);
        const roleId: string | null = safeParseNullDefault<string>(object?._roleId, '');
        return new UserChecklistElement(elementId, name, itemNumber, sortNumber, currentAsDefault, roleId);

    } else if (object?._editorType === EditorType.DROPDOWN) {
        const defaultValue: string | null = safeParseNullDefault<string>(object?._defaultValue, '');
        const dependentElementId: string | null = safeParseNullDefault<string>(object?._dependentElementId, '');

        if (!Array.isArray(object?._options) || !Array.isArray(object?._itemEnableMask) || !Array.isArray(object?._dependencyMapping)) {
            return null;
        }

        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-explicit-any
        const options: (string | null)[] = object?._options.map((option: any) => safeParseNullDefault<string>(option, '')) ?? [];
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-explicit-any
        const itemEnableMask: (boolean | null)[] = object?._itemEnableMask.map((itemEnableMaskElement: any) => safeParseNullDefault<boolean>(itemEnableMaskElement, false)) ?? [];
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-explicit-any
        const dependencyMapping: (string[] | null)[] = object?._dependencyMapping.map((dependencyMappingElement: any) => {
            
            if (!Array.isArray(dependencyMappingElement)) {
                return null;
            }
            
            const dependencyMappingElementArray: (string | null)[] = dependencyMappingElement.map((dependencyMappingSubElement) => safeParseNullDefault<string>(dependencyMappingSubElement, ''));
            
            return removeNulls(dependencyMappingElementArray);
        });
        
        return new DropdownChecklistElement(elementId, name, itemNumber, sortNumber, removeNulls(options), defaultValue, removeNulls(itemEnableMask), dependentElementId, removeNulls(dependencyMapping));

    } else if (object?._editorType === EditorType.INSPECTING_COMPANY) {
        return new InspectingCompanyChecklistElement(elementId, name, itemNumber, sortNumber);

    } else if (object?._editorType === EditorType.CATEGORY) {
        const parentCategoryId: string | null = safeParseNullDefault<string>(object?._parentCategoryId, '');

        if (parentCategoryId === null || parentCategoryId === '') {
            return null;
        }

        return new CategoryChecklistElement(elementId, name, itemNumber, sortNumber, parentCategoryId);

    } else if (object?._editorType === EditorType.DEPARTMENT) {
        const dependentElementId: string | null = safeParseNullDefault<string>(object?._dependentElementId, '');

        return new DepartmentChecklistElement(elementId, name, itemNumber, sortNumber, dependentElementId);

    } else if (object?._editorType === EditorType.LOOKUP) {
        const lookupType: string | null = safeParseNullDefault<string>(object?._lookupType, '');
        const dependentElementId: string | null = safeParseNullDefault<string>(object?._dependentElementId, '');

        if (lookupType === null || dependentElementId === null) {
            return null;
        }
        
        return new LookupChecklistElement(elementId, name, itemNumber, sortNumber, lookupType, dependentElementId);

    }else {
        return null;
    }
}